import { useState } from 'react';
import { App, Button, Card, Form, Input, Space, Typography, Select } from 'antd';
import { useNavigate } from 'react-router-dom';
import { trpc } from '../../lib/api';

const { Title, Text } = Typography;

type CompanyFormValues = {
  companyName: string;
  companyEmail?: string;
  companyPhone?: string;
  assignedManagerUserId?: string;
  primaryUserName: string;
  primaryUserEmail: string;
};

export function PartnerCompanyCreatePage() {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const [form] = Form.useForm<CompanyFormValues>();
  const [submitting, setSubmitting] = useState(false);
  const utils = trpc.useUtils();
  const {
    data: partnerUsersData,
    error: partnerUsersError,
    isLoading: partnerUsersLoading,
  } = trpc.partnerAdmin.partnerListUsers.useQuery(
    { page: 1, limit: 100 },
    { retry: false }
  );

  const registerMutation = trpc.partnerPortal.registerCompany.useMutation({
    onSuccess: (result) => {
      message.success('会社を登録しました');
      void utils.partnerPortal.listAccessibleCompanies.invalidate();
      navigate(`/partner/companies/${result.company.id}`);
    },
    onError: (error) => {
      message.error(error.message || '会社登録に失敗しました');
    },
    onSettled: () => {
      setSubmitting(false);
    },
  });

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      setSubmitting(true);
      registerMutation.mutate({
        company: {
          name: values.companyName,
          email: values.companyEmail ?? null,
          phone: values.companyPhone ?? null,
        },
        access: {
          assignedManagerUserId: values.assignedManagerUserId ?? null,
        },
        primaryUser: {
          email: values.primaryUserEmail,
          name: values.primaryUserName,
        },
      });
    });
  };

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={3} style={{ margin: 0 }}>新規会社登録</Title>
        <Button onClick={() => navigate('/partner/companies')}>担当会社へ戻る</Button>
      </div>

      <Card>
        <Form form={form} layout="vertical">
          <Title level={5} style={{ marginTop: 0 }}>会社情報</Title>
          <Form.Item
            name="companyName"
            label="会社名"
            rules={[{ required: true, message: '会社名を入力してください' }]}
          >
            <Input placeholder="会社名" />
          </Form.Item>
          <Form.Item name="companyEmail" label="会社メールアドレス">
            <Input placeholder="info@example.com" />
          </Form.Item>
          <Form.Item name="companyPhone" label="会社電話番号">
            <Input placeholder="03-0000-0000" />
          </Form.Item>

          <Title level={5} style={{ marginTop: 24 }}>代理店担当者</Title>
          <Form.Item name="assignedManagerUserId" label="担当者（代理店側）">
            <Select
              allowClear
              placeholder="担当者を選択（任意）"
              options={(partnerUsersData?.users ?? []).map((user) => ({
                value: user.id,
                label: `${user.name} (${user.email})${user.isActive ? '' : '（招待中）'}`,
                disabled: !user.isActive,
              }))}
              showSearch
              optionFilterProp="label"
              loading={partnerUsersLoading}
            />
          </Form.Item>
          {partnerUsersError && (
            <Text type="secondary">
              担当者一覧を取得できません。代理店管理者でログインしているか確認してください。
            </Text>
          )}

          <Title level={5} style={{ marginTop: 24 }}>初期ユーザー</Title>
          <Form.Item
            name="primaryUserName"
            label="担当者名"
            rules={[{ required: true, message: '担当者名を入力してください' }]}
          >
            <Input placeholder="山田 太郎" />
          </Form.Item>
          <Form.Item
            name="primaryUserEmail"
            label="担当者メールアドレス"
            rules={[
              { required: true, message: '担当者メールアドレスを入力してください' },
              { type: 'email', message: '有効なメールアドレスを入力してください' },
            ]}
          >
            <Input placeholder="admin@example.com" />
          </Form.Item>

          <Text type="secondary">
            登録後、担当者へ招待メールが送信されます。
          </Text>

          <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end' }}>
            <Space>
              <Button onClick={() => navigate('/partner/companies')}>キャンセル</Button>
              <Button type="primary" onClick={handleSubmit} loading={submitting}>
                登録する
              </Button>
            </Space>
          </div>
        </Form>
      </Card>
    </Space>
  );
}
